import React, { useCallback, useContext, useEffect } from 'react';
import { Image, ImageBackground, Pressable, StyleSheet, View, Text } from 'react-native';
import { useTheme, Icon, ListItem } from '@rneui/themed';
import TouchableScale from 'react-native-touchable-scale';
import { findActive } from '../../common/helpers/Navigation';
import AppContext, { AppActions, AppEvents } from '../../contexts/App';
import LayoutContext from '../../contexts/Layout';
import { fetchPage } from '../../common/Api';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

const PrimaryNav = ({ nodeTypeAlias = 'reactMediaSlide' }) => {
    const { state, dispatch } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);
    const { theme } = useTheme();
    const isCollapsed = state.navigation.primaryIsCollapsed;

    const primaryLinksRight = useSharedValue(styles.primaryLinksContainer.animate.from.right);
    const logoBlur = useSharedValue(styles.logoContainer.animate.from.blur);
    const logoOpacity = useSharedValue(styles.logoContainer.animate.from.opacity);
    const videoOpacity = useSharedValue(styles.videoContainer.animate.from.opacity);
    const menuBtnLeft = useSharedValue(styles.menuBtnContainer.animate.from.left);
    const primaryLinksStyle = useAnimatedStyle(() => {
        return {
            right: primaryLinksRight.value
        };
    });
    const logoAnimationStyle = useAnimatedStyle(() => {
        return {
            filter: `blur(${logoBlur.value})`,
            opacity: logoOpacity.value
        };
    });
    const videoBackgroundStyle = useAnimatedStyle(() => {
        return {
            opacity: videoOpacity.value
        };
    });
    const menuAnimationStyle = useAnimatedStyle(() => {
        return {
            left: menuBtnLeft.value
        };
    });

    useEffect(() => {
        if (isCollapsed === true) {
            primaryLinksRight.value = withTiming(styles.primaryLinksContainer.animate.to.right, { duration: styles.primaryLinksContainer.animate.from.duration });
            logoBlur.value = withTiming(styles.logoContainer.animate.to.blur, { duration: styles.logoContainer.animate.from.duration });
            logoOpacity.value = withTiming(styles.logoContainer.animate.to.opacity, { duration: styles.logoContainer.animate.from.duration });
            videoOpacity.value = withTiming(styles.videoContainer.animate.to.opacity, { duration: styles.videoContainer.animate.from.duration });
            menuBtnLeft.value = withTiming(styles.menuBtnContainer.animate.to.left, { duration: styles.menuBtnContainer.animate.from.duration });
        } else {
            primaryLinksRight.value = withTiming(styles.primaryLinksContainer.animate.from.right, { duration: styles.primaryLinksContainer.animate.to.duration });
            logoBlur.value = withTiming(styles.logoContainer.animate.from.blur, { duration: styles.logoContainer.animate.to.duration });
            logoOpacity.value = withTiming(styles.logoContainer.animate.from.opacity, { duration: styles.logoContainer.animate.to.duration });
            videoOpacity.value = withTiming(styles.videoContainer.animate.from.opacity, { duration: styles.videoContainer.animate.to.duration });
            menuBtnLeft.value = withTiming(styles.menuBtnContainer.animate.from.left, { duration: styles.menuBtnContainer.animate.to.duration });
        }
    }, [isCollapsed, primaryLinksRight, logoBlur, logoOpacity, videoOpacity, menuBtnLeft]);

    const getParentPath = useCallback(() => {
        const pathname = window.location.pathname;

        if (pathname.charCount('/') > 1) {
            return `/${pathname.slice(1).split('/')[0]}`;
        }

        return '/';
    }, []);

    const renderLinks = textColor => {
        if (state.navigation?.primary && Object.keys(state.navigation.primary).length > 0) {
            const active = findActive(state.action?.href || window.location.pathname);

            return (
                <View style={styles.renderLinks}>
                    {state.navigation?.primary?.map(link => {
                        if (link.doNotIncludeInNavigation) {
                            return null;
                        }

                        const id = link?.id;
                        const title = link?.title;
                        const rootPath = link?.rootPath;
                        const isActive = rootPath.equals(active) || (rootPath.length > 1 && rootPath.replaceAll('/', '').toLowerCase().equals(active));
                        const activeStyle = isActive
                            ? {
                                  textShadowColor: theme?.colors?.white,
                                  textShadowOffset: { height: 0, width: 0 },
                                  textShadowRadius: 4
                              }
                            : {};

                        const onPress = async e => {
                            e.preventDefault();

                            if (state.search?.event?.equals(AppEvents.SEARCH_QUERY_SUBMITTED)) {
                                dispatch({
                                    type: AppActions.SEARCH_STATE_CHANGE,
                                    event: AppEvents.SEARCH_QUERY_CLOSED
                                });
                            }

                            if (!link.navigationOnly) {
                                await fetchPage(dispatch, link.rootPath);
                            } else {
                                dispatch({
                                    href: link.rootPath,
                                    data: { ...link },
                                    type: AppActions.NAVIGATE
                                });
                            }
                        };

                        return (
                            <ListItem
                                activeScale={0.95}
                                containerStyle={{ backgroundColor: 'transparent', paddingRight: 0 }}
                                friction={90}
                                key={id}
                                onPress={onPress}
                                tension={100}
                                Component={TouchableScale}
                            >
                                <ListItem.Content>
                                    <View style={{ alignItems: 'center', flexDirection: 'row', cursor: 'pointer' }}>
                                        <ListItem.Title style={[activeStyle, theme.navigation.web.Primary.listItemTitle, { color: textColor }]}>{title}</ListItem.Title>
                                        {isActive && <Icon {...theme.navigation.web.Primary.listItemIcon} />}
                                    </View>
                                </ListItem.Content>
                            </ListItem>
                        );
                    })}
                </View>
            );
        }
    };

    if (state.siteWrapper) {
        const config = state.siteWrapper.web?.contentItems?.['primary Navigation'];
        const imageSrc = config?.image?.cdnImagePath;
        const internalVideo = config?.internalVideo?.cdnImagePath;
        const labelColor = config?.textColor;
        // const logoSrc = config?.logo?.cdnImagePath;
        const videoColor = config?.videoTextColor ?? '#fff';
        const color = labelColor || '#fff';

        if (imageSrc) {
            return (
                <ImageBackground
                    dataSet={{ umbracoComponentName: nodeTypeAlias }}
                    imageStyle={{ height: '100%', width: '100%' }}
                    source={{ uri: `https:${imageSrc}` }}
                    style={[
                        {
                            height: layout.device.height
                        }
                    ]}
                >
                    <Pressable style={{ flexDirection: 'row', justifyContent: 'center' }} onPress={async () => await fetchPage(dispatch, '/')}>
                        <Image placeholderStyle={{ opacity: 0 }} source={require('../../../assets/images/logo.svg')} style={styles.logo} />
                    </Pressable>
                    {renderLinks(color)}
                </ImageBackground>
            );
        }

        if (internalVideo) {
            return (
                <View dataSet={{ umbracoComponentName: nodeTypeAlias }} style={[{ height: layout.dimensions.height, overflow: 'hidden' }]}>
                    <Animated.View style={[styles.videoContainer, videoBackgroundStyle]}>
                        <video autoPlay={!state?.settings?.websiteSettings?.environment?.equals('development')} loop={true} muted={true} style={styles.video}>
                            <source src={`https:${internalVideo}`} />
                        </video>
                    </Animated.View>
                    <Animated.View style={[styles.primaryLinksContainer, primaryLinksStyle]}>{renderLinks(videoColor)}</Animated.View>
                    <Animated.View style={[styles.menuBtnContainer, menuAnimationStyle]}>
                        <Pressable
                            // accessibilityRole="button" <-- this will cause a validateDOMNesting warning @jackson
                            accessibilityLabel='Open menu'
                            hitSlop={8}
                            onPress={() => {
                                dispatch({
                                    href: getParentPath(),
                                    type: AppActions.NAVIGATE_TO_PARENT
                                });
                            }}
                            style={({ hovered, focused, pressed }) => [
                                {
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 4,
                                    cursor: 'pointer',
                                    paddingVertical: 6,
                                    paddingHorizontal: 8,
                                    backgroundColor: 'transparent',
                                    opacity: pressed ? 0.75 : 1,
                                    transform: hovered ? [{ translateY: -1 }] : undefined,
                                    outlineStyle: 'none'
                                },
                                focused && {
                                    outlineWidth: 2,
                                    outlineColor: '#fff',
                                    outlineOffset: 2,
                                    outlineStyle: 'solid'
                                }
                            ]}
                        >
                            <Icon name='chevron-left-circle-outline' type='material-community' size={55} color='#fff' />
                            {/*@tim.cook lmk if we need the modify these silver-bullet settings. I replaced the button to quell the react error @jacksonkr */}
                            <Text style={{ marginTop: '1px', color: '#fff', fontSize: '20px' }}>Menu</Text>
                        </Pressable>
                    </Animated.View>
                    <View>
                        <Animated.View style={[styles.logoContainer, logoAnimationStyle]}>
                            <Pressable style={{ flexDirection: 'row', justifyContent: 'center' }} onPress={async () => await fetchPage(dispatch, '/')}>
                                <Image
                                    placeholderStyle={{ opacity: 0 }}
                                    source={require('../../../assets/images/logo.svg')}
                                    style={[styles.logo, { marginTop: layout.styleGuide.spacer2 }]}
                                />
                            </Pressable>
                        </Animated.View>
                        <Animated.View style={[styles.logoContainer]}>
                            <Pressable style={{ flexDirection: 'row', justifyContent: 'center' }} onPress={async () => await fetchPage(dispatch, '/')}>
                                <Image
                                    placeholderStyle={{ opacity: 0 }}
                                    source={require('../../../assets/images/icon.svg')}
                                    style={[styles.logo, { marginTop: layout.styleGuide.spacer2 }]}
                                />
                            </Pressable>
                        </Animated.View>
                    </View>
                </View>
            );
        }
    }

    return (
        <>
            <View dataSet={{ umbracoComponentName: nodeTypeAlias }} style={[styles.container]}>
                {renderLinks('#fff')}
            </View>
            <View
                style={{
                    alignContent: 'center',
                    justifyContent: 'center',
                    paddingHorizontal: 3
                }}
            />
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#000',
        justifyContent: 'flex-end',
        overflow: 'hidden'
    },
    listItemTitle: {
        fontFamily: 'GothamBook',
        color: 'white',
        fontSize: '16pt',
        lineHeight: '18pt',
        marginBottom: 0,
        marginRight: 10
    },
    listItemIcon: {
        color: '#fff',
        name: 'caret-left',
        size: 20,
        type: 'font-awesome-5'
    },
    logoContainer: {
        position: 'absolute',
        width: '100%',
        top: 0,
        animate: {
            from: {
                duration: 750,
                blur: '0px',
                opacity: 1
            },
            to: {
                duration: 750,
                blur: '10px',
                opacity: 0.0
            }
        }
    },
    logo: {
        height: 98,
        width: 168
    },
    menuBtnContainer: {
        animate: {
            from: {
                duration: 750,
                left: '-30vw'
            },
            to: {
                duration: 750,
                left: '0vw'
            }
        },
        position: 'absolute',
        display: 'flex',
        height: '100%',
        width: '100%',
        justifyContent: 'center'
    },
    primaryLinksContainer: {
        animate: {
            from: {
                duration: 750,
                right: '0vw'
            },
            to: {
                duration: 750,
                right: '-60vw'
            }
        },
        position: 'absolute',
        display: 'flex',
        height: '100%',
        justifyContent: 'center'
    },
    renderLinks: { alignItems: 'flex-end', justifyContent: 'center' },
    videoContainer: {
        animate: {
            from: {
                duration: 750,
                opacity: 1
            },
            to: {
                duration: 750,
                opacity: 0
            }
        },
        position: 'absolute',
        height: '100%',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)'
    },
    video: {
        height: '100%',
        objectFit: 'fill'
    }
});

export default PrimaryNav;
